import { AlertTriangle, Pencil, Search, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { area } from "@turf/turf";
import {
  hasPositiveArea,
  isTooDetailedToEdit,
  isUploadedShape,
  MAX_EDITABLE_VERTICES,
  partitionViewOnly,
  UploadedBoundarySummary,
  useBoundaryFeatures,
} from "components/boundary";
import { SELECT_DRAW_MODE } from "components/boundary/draw/draw-modes";
import {
  zoomToFeature,
  zoomToFeatureCollection,
} from "pages/geoscape-data/explorer/utils";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ClipConfigProps, ClipStepsControlProps } from "../../clip-menu";
import { BoundaryComponent } from "./boundary-component";
import { BoundaryItem } from "./boundary-item";
import { DrawComponent } from "./draw-component";
import { UploadComponent } from "./upload-component";

export type FeatureOrFeatureArray =
  GeoJSON.Feature<GeoJSON.Geometry> | GeoJSON.Feature<GeoJSON.Geometry>[];

export interface BoundaryToolProps {
  selectedFeature: string | null;
  setSelectedFeature: React.Dispatch<React.SetStateAction<string | null>>;
  addOrUpdateFeatures: (feat: FeatureOrFeatureArray) => void;
  currentMode: string;
  setCurrentMode: React.Dispatch<React.SetStateAction<string>>;
  // A clip holds one uploaded file; the Upload tab stays closed while it has one.
  hasUploadedBoundary?: boolean;
}

// Keyed by geometry object: a vertex drag keeps untouched geometries, so only the edited shape is re-measured.
const areaCache = new WeakMap<GeoJSON.Geometry, number>();
const areaOf = (feature: GeoJSON.Feature<GeoJSON.Geometry>): number => {
  let value = areaCache.get(feature.geometry);
  if (value === undefined) {
    value = area(feature);
    areaCache.set(feature.geometry, value);
  }
  return value;
};

const boundarySteps = [
  { stepName: "Upload", element: UploadComponent },
  { stepName: "Draw", element: DrawComponent },
  { stepName: "Boundary", element: BoundaryComponent },
];

export const DefineBoundary = (
  props: ClipConfigProps & ClipStepsControlProps,
) => {
  // A clip reopened while its upload is processing or failed opens on Upload, which shows that status.
  const [component, setComponent] = useState(
    props.clipConfig.geometryProcessing === "processing" ||
      props.clipConfig.geometryProcessing === "failed"
      ? "Upload"
      : "Draw",
  );

  const onGeometryChange = useCallback(
    (geometry: GeoJSON.FeatureCollection | undefined) => {
      const features = geometry?.features ?? [];
      const drawn = features.filter((f) => !isUploadedShape(f));
      props.setClipConfig((prev) => {
        const hadUpload = !!prev.geometry?.features.some(isUploadedShape);
        const keepsUpload = drawn.length < features.length;
        return {
          ...prev,
          geometry: geometry ?? { type: "FeatureCollection", features: [] },
          boundaryChange: keepsUpload
            ? prev.boundaryChange
            : drawn.length
              ? { drawn: { type: "FeatureCollection", features: drawn } }
              : hadUpload || prev.geometry_s3_path || prev.geometry?.features.length
                ? { clear: true }
                : prev.boundaryChange,
        };
      });
    },
    [props.setClipConfig],
  );

  const {
    features,
    selectedFeatureId,
    currentMode,
    setCurrentMode,
    addOrUpdateFeatures,
    handleDelete,
    handleDeleteMany,
    handleRename,
    handleSelect: baseHandleSelect,
  } = useBoundaryFeatures({
    draw: props.draw,
    mapRef: props.mapRef as React.RefObject<any>,
    initialGeometry: props.clipConfig.geometry,
    onGeometryChange,
  });

  // Done here, not in the Upload tab, so it also runs if the clip was reopened mid-processing or on another tab.
  const previousProcessing = useRef(props.clipConfig.geometryProcessing);
  useEffect(() => {
    const wasProcessing = previousProcessing.current === "processing";
    previousProcessing.current = props.clipConfig.geometryProcessing;
    const geometry = props.clipConfig.geometry;
    if (
      !wasProcessing ||
      props.clipConfig.geometryProcessing !== "ready" ||
      !geometry?.features.length
    )
      return;
    addOrUpdateFeatures(geometry.features as FeatureOrFeatureArray);
    props.draw.add(geometry.features);
    zoomToFeatureCollection(props.mapRef, geometry, 40);
  }, [props.clipConfig.geometryProcessing, props.clipConfig.geometry]);

  // Extend select to also zoom to feature
  const handleSelectSidebar = useCallback(
    (id: string) => {
      baseHandleSelect(id);
      const selectedFeature = features.find((x) => x.id === id);
      if (selectedFeature) {
        zoomToFeature(props.mapRef, selectedFeature.geometry);
      }
    },
    [baseHandleSelect, features, props.mapRef],
  );

  // Walks every vertex of the clip, so it only reruns when the shapes change.
  const {
    clipTooHeavy,
    uploadedCount,
    tooDetailedCount,
    readOnlyIds,
    isAreaTooBig,
    areas,
    drawnFeatures,
    uploadedFeatures,
    uploadedArea,
  } = useMemo(() => {
    // Same rule BoundaryDraw uses to route shapes, so the list matches the map.
    const { readOnly, clipTooHeavy } = partitionViewOnly(features);
    const drawn = features.filter((f) => !isUploadedShape(f));
    // Uploaded shapes are read-only by design, so the "too detailed" warning only counts drawn shapes.
    const tooDetailedCount = clipTooHeavy
      ? drawn.length
      : drawn.filter(isTooDetailedToEdit).length;
    const areas = new Map(features.map((f) => [f.id as string, areaOf(f)]));
    const totalArea = [...areas.values()].reduce((sum, a) => sum + a, 0);
    const uploaded = features.filter(isUploadedShape);
    return {
      clipTooHeavy,
      uploadedCount: uploaded.length,
      tooDetailedCount,
      readOnlyIds: new Set(readOnly.map((f) => f.id as string)),
      isAreaTooBig: totalArea / 1000000 > 100,
      areas,
      drawnFeatures: drawn,
      uploadedFeatures: uploaded,
      uploadedArea: uploaded.reduce(
        (sum, f) => sum + (areas.get(f.id as string) ?? 0),
        0,
      ),
    };
  }, [features]);
  const isProcessing = props.clipConfig.geometryProcessing === "processing";
  const hasUploadedBoundary = uploadedFeatures.length > 0;
  // A boundary is one upload or drawn shapes, never both: an upload closes Draw, drawn shapes close Upload.
  const drawLocked = isProcessing || hasUploadedBoundary;
  const uploadLocked = !drawLocked && drawnFeatures.length > 0;
  // A drawing tool picked before uploading would stay active on the map, so drop back to select.
  useEffect(() => {
    if (drawLocked && currentMode !== SELECT_DRAW_MODE) {
      props.draw.changeMode(SELECT_DRAW_MODE);
    }
  }, [drawLocked]);
  const activeComponent = drawLocked
    ? "Upload"
    : uploadLocked && component === "Upload"
      ? "Draw"
      : component;

  const hasValidGeometry =
    features.length > 0 && features.every(hasPositiveArea);
  const canConfirm =
    !isProcessing &&
    (hasValidGeometry ||
      (features.length === 0 && Boolean(props.clipConfig.pending_zip_s3_key)));

  const typeCards = [
    {
      key: "Upload",
      icon: Upload,
      label: "Upload",
      sublabel: "ZIP boundary file",
      disabled: uploadLocked,
    },
    {
      key: "Draw",
      icon: Pencil,
      label: "Draw",
      sublabel: "Boundary",
      disabled: drawLocked,
    },
    {
      key: "Boundary",
      icon: Search,
      label: "Boundary",
      sublabel: "Search",
      disabled: true,
    },
  ];

  return (
    <div className="flex flex-col gap-2 h-full">
      <h5 className="text-lg font-medium">Define Clip Boundary</h5>
      {uploadedCount > 0 && (
        <Alert>
          <AlertDescription>
            The uploaded boundary is shown read-only with a dashed outline and
            used as it is for your quote. To change it, remove it below and
            upload the corrected file.
          </AlertDescription>
        </Alert>
      )}
      {tooDetailedCount > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {clipTooHeavy
              ? "This boundary has too much detail to edit here"
              : tooDetailedCount === 1
                ? `1 shape is too detailed to edit here (over ${MAX_EDITABLE_VERTICES.toLocaleString()} points)`
                : `${tooDetailedCount} shapes are too detailed to edit here (over ${MAX_EDITABLE_VERTICES.toLocaleString()} points)`}
            . It is shown with a dashed outline and will still be used for your
            quote. To change it, simplify it in your GIS software and upload it
            again.
          </AlertDescription>
        </Alert>
      )}
      <div className="flex flex-row gap-2 justify-between">
        {typeCards.map((card) => {
          const Icon = card.icon;
          const isSelected = activeComponent === card.key;
          return (
            <button
              key={card.key}
              data-cy={card.key.toLowerCase()}
              disabled={card.disabled}
              onClick={() => !card.disabled && setComponent(card.key)}
              className={`w-[100px] h-[136px] flex flex-col justify-center items-center gap-1 rounded-lg border cursor-pointer transition-colors ${
                card.disabled
                  ? "opacity-40 pointer-events-none"
                  : isSelected
                    ? "bg-primary/10 border-primary"
                    : "hover:bg-muted"
              }`}
            >
              <div className="size-10 rounded-full bg-info/10 flex items-center justify-center">
                <Icon className="size-5 text-info" />
              </div>
              <span className="text-sm font-medium">{card.label}</span>
              <span className="text-xs text-muted-foreground">
                {card.sublabel}
              </span>
            </button>
          );
        })}
      </div>
      <div>
        {React.createElement(
          boundarySteps.filter((b) => b.stepName === activeComponent)[0].element,
          {
            selectedFeature: selectedFeatureId,
            setSelectedFeature: () => {},
            addOrUpdateFeatures,
            currentMode,
            setCurrentMode,
            hasUploadedBoundary,
            ...props,
          },
        )}
      </div>

      <div className="flex flex-col gap-2 overflow-auto flex-1 min-h-0">
        <div className="flex flex-col gap-2">
          {drawnFeatures.map((feature, idx) => (
            <BoundaryItem
              key={feature.id as string}
              id={feature.id as string}
              selected={feature.id == selectedFeatureId}
              name={feature.properties?.name ?? `Boundaries ${idx}`}
              area={areas.get(feature.id as string) ?? 0}
              viewOnly={readOnlyIds.has(feature.id as string)}
              onDelete={handleDelete}
              onEdit={handleRename}
              onSelect={handleSelectSidebar}
            />
          ))}
          {/* An upload is one unit: a single row removes the whole upload; its shapes stay on the map. */}
          {uploadedFeatures.length > 0 && (
            <UploadedBoundarySummary
              count={uploadedFeatures.length}
              area={uploadedArea}
              onRemove={() =>
                handleDeleteMany(uploadedFeatures.map((f) => f.id as string))
              }
            />
          )}
        </div>
      </div>
      {!hasValidGeometry && features.length > 0 && (
        <Alert>
          <AlertTriangle className="size-4" />
          <AlertDescription>
            Each boundary must have a positive area. Please redraw or remove
            any empty boundary before continuing.
          </AlertDescription>
        </Alert>
      )}
      {isAreaTooBig && (
        <Alert>
          <AlertTriangle className="size-4" />
          <AlertDescription>
            For large boundaries a commercial license could be more suitable.
          </AlertDescription>
        </Alert>
      )}
      <div className="self-end mt-auto flex">
        <Button
          disabled={!canConfirm}
          onClick={() => {
            props.setActiveStep(Math.min(props.activeStep + 1, props.maxSteps));
          }}
        >
          Confirm
        </Button>
      </div>
    </div>
  );
};
